import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Heart, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const GoogleAuthCallback = () => {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Completing sign in with Google...');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleCallback = async () => {
      const token = searchParams.get('token');
      const redirect = searchParams.get('redirect') || '/dashboard';
      const error = searchParams.get('error');
      const errorMessage = searchParams.get('message');

      if (error) {
        setStatus('error');
        setMessage(errorMessage || 'Google sign in failed. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (token) {
        try {
          // Store the token
          localStorage.setItem('token', token);
          
          // Verify token works by fetching user data - use current origin
          const apiUrl = `${window.location.origin}/api/auth/me`;
          await axios.get(apiUrl, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setStatus('success');
          setMessage('Successfully signed in with Google!');
          
          // Handle campaign creation redirect
          if (redirect === '/create-campaign' || redirect === 'create-campaign') {
            const pendingCampaign = localStorage.getItem('pendingCampaign');
            if (pendingCampaign) {
              localStorage.setItem('pendingCampaignSubmit', 'true');
            }
          }
          
          // Redirect after a brief delay - force page reload to update auth state
          setTimeout(() => {
            window.location.href = redirect.startsWith('/') ? redirect : `/${redirect}`;
          }, 1500);
        } catch (err) {
          console.error('Failed to complete Google auth:', err);
          setStatus('error');
          setMessage('Failed to complete sign in. Please try again.');
          localStorage.removeItem('token');
          setTimeout(() => navigate('/login'), 3000);
        }
      } else {
        setStatus('error');
        setMessage('No authentication token received. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-primary/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Heart className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-bold text-foreground">FundTheHope</span>
          </div>
        </div>

        <Card className="shadow-xl border-0">
          <CardContent className="p-8 text-center">
            {status === 'processing' && (
              <>
                <Loader2 className="w-16 h-16 mx-auto mb-4 text-emerald-500 animate-spin" />
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Signing you in...
                </h2>
                <p className="text-muted-foreground">{message}</p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Welcome!
                </h2>
                <p className="text-muted-foreground">{message}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Redirecting to your dashboard...
                </p>
              </>
            )}

            {status === 'error' && (
              <>
                <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Sign In Failed
                </h2>
                <p className="text-muted-foreground">{message}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Redirecting to login page...
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GoogleAuthCallback;
